import { useEffect } from 'react';

import { ButtonProps } from '@components/button';
import { Form, FormProps, useFormContext } from '@components/form';
import {
  useUnsavedChangesModal,
  UnsavedChangesPromptModal,
} from '@components/generic-modals/unsaved-changes-modal';
import {
  forceHideModal,
  hideModal,
  useConfirmationModal,
  useModalEventHandlers,
} from '@components/modal';
import {
  forceHideSidebar,
  useSidebarEventHandlers,
  useSidebarReadOnlyContext,
  useSidebarState,
} from '@components/sidebar';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { noop } from '@utils/browser';

const continueRegistry = {
  id: '',
  onContinue: noop,
};

const clearContinueRegistry = () => {
  continueRegistry.id = '';
  continueRegistry.onContinue = noop;
};

export const setContinueRegistry = ({ id, onContinue: callback }: typeof continueRegistry) => {
  continueRegistry.id = id;
  continueRegistry.onContinue = callback;
};

export const FormWithUnsavedChangesPrompt = ({ children, ...rest }: FormProps) => (
  <Form {...rest}>
    <UnsavedChangesPrompter onSubmit={rest.onSubmit} />
    {children}
  </Form>
);

type UnsavedChangesPromptOptions = {
  discardText?: string;
  discardButtonVariant?: ButtonProps['variant'];
};

type UnsavedChangesPrompterProps = Pick<FormProps, 'onSubmit'> & {
  overwriteIsDirty?: boolean;
  locale?: typeof actionUnsavedChangesLocale;
} & UnsavedChangesPromptOptions;

export const UnsavedChangesPrompter = ({
  onSubmit,
  overwriteIsDirty,
  locale,
  discardText,
  discardButtonVariant,
}: UnsavedChangesPrompterProps) => {
  const { store } = useFormContext();
  const isReadOnly = useSidebarReadOnlyContext();

  const onSubmitEvent = useEvent(() => onSubmit(store.getState().values, 'save'));
  const showModal = useUnsavedChangesPromptModal(
    () => store.actions.submit({ onSubmit: onSubmitEvent, action: 'submit' }),
    locale,
    { discardText, discardButtonVariant },
  );
  const { setOnBeforeChange } = useSidebarEventHandlers();
  const isDirty = overwriteIsDirty ?? store.useIsDirty();

  useEffect(() => {
    if (isReadOnly) {
      setOnBeforeChange(undefined);
      return () => setOnBeforeChange(undefined);
    }
    if (isDirty) {
      setOnBeforeChange(() => {
        showModal();
        return false;
      });
    } else {
      setOnBeforeChange(undefined);
    }
    return () => setOnBeforeChange(undefined);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isReadOnly]);

  return null;
};

export const useUnsavedChangesPromptModal = (
  onSave: () => Promise<boolean>,
  locale?: typeof actionUnsavedChangesLocale,
  options?: UnsavedChangesPromptOptions,
) => {
  const { t } = useScopedTranslate(locale ?? actionUnsavedChangesLocale);
  const { activeId } = useSidebarState();
  const showModal = useUnsavedChangesModal();

  const handleContinue = () => {
    forceHideSidebar();
    if (activeId !== continueRegistry.id) {
      setTimeout(continueRegistry.onContinue);
    }
  };

  const handleSave = async () => {
    try {
      const isSaved = await onSave();
      if (isSaved) {
        handleContinue();
      } else {
        hideModal();
      }
    } catch (e) {
      hideModal();
    } finally {
      clearContinueRegistry();
    }
  };

  return () =>
    showModal(
      {
        onDiscard: handleContinue,
        onSave: handleSave,
        title: t('saveChangesTitle'),
        description: t('hasUnsaved'),
        saveText: t('saveChanges'),
        discardText: options?.discardText,
        discardButtonVariant: options?.discardButtonVariant,
      },
      {
        onBeforeChange: clearContinueRegistry,
      },
    );
};

const actionUnsavedChangesLocale = {
  en: {
    saveChangesTitle: 'Save changes?',
    hasUnsaved: 'This action has unsaved changes.\nWould you like to save or discard them?',
    saveChanges: 'Save action',
  },
};

export const ModalUnsavedChangesPrompter = ({
  onSubmit,
  overwriteIsDirty,
  isSaveDisabled,
}: Pick<FormProps, 'onSubmit'> & {
  overwriteIsDirty?: boolean;
  isSaveDisabled?: boolean;
}) => {
  const { store } = useFormContext();
  const { t } = useScopedTranslate(actionUnsavedChangesLocale);
  const showConfirmation = useConfirmationModal(UnsavedChangesPromptModal);
  const { setOnBeforeChange } = useModalEventHandlers();
  const isDirty = overwriteIsDirty ?? store.useIsDirty();
  const onSubmitEvent = useEvent(() => onSubmit(store.getState().values, 'save'));

  useEffect(() => {
    if (isDirty) {
      setOnBeforeChange(() => {
        showConfirmation({
          title: t('saveChangesTitle'),
          description: t('hasUnsaved'),
          isSaveDisabled,
          onSave: () => store.actions.submit({ onSubmit: onSubmitEvent, action: 'submit' }),
          onDiscard: forceHideModal,
        });
        return false;
      });
    } else {
      setOnBeforeChange(undefined);
    }
    return () => setOnBeforeChange(undefined);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isSaveDisabled]);

  return null;
};
